import React from 'react';

/**
 * iter68 — Top-level ErrorBoundary.
 *
 * Without this, any uncaught render error inside a route (e.g. a
 * `.map()` on undefined when an API returns an unexpected shape, or a
 * missing `process.env.REACT_APP_BACKEND_URL` at build time) takes the
 * entire React tree down to a blank screen. Mounting one at the root
 * ensures we ALWAYS show the user something they can act on — a calm
 * error card with a "Try again" button + a "Go home" link — plus we
 * surface the error to the console so it's still debuggable.
 *
 * The boundary deliberately re-keys its children on retry, which
 * forces an unmount + fresh mount of the entire subtree (cheapest
 * recovery that works for stale state + transient API failures).
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep this — production users / support can ask for a screenshot
    // of the console.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] caught render error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState((s) => ({ error: null, errorInfo: null, retryKey: s.retryKey + 1 }));
  };

  render() {
    if (this.state.error) {
      const msg = (this.state.error && this.state.error.message) || 'Something went wrong.';
      return (
        <div
          className="min-h-screen flex items-center justify-center px-6"
          style={{ background: '#F7F2E7', color: '#1A3626' }}
          data-testid="app-error-boundary"
        >
          <div
            className="max-w-md w-full text-center"
            style={{
              border: '1px solid rgba(26,54,38,0.18)',
              borderRadius: 14,
              padding: '36px 28px',
              background: '#FFFFFF',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#C5A059',
                fontWeight: 600,
              }}
            >
              MyLeaf Legacy
            </div>
            <h1
              style={{
                fontFamily: '"Playfair Display", "Cormorant Garamond", serif',
                fontSize: 28,
                marginTop: 14,
                color: '#1A3626',
                fontWeight: 500,
              }}
            >
              Something went quiet for a moment.
            </h1>
            <p
              style={{
                marginTop: 14,
                fontSize: 14,
                lineHeight: 1.5,
                color: 'rgba(26,54,38,0.72)',
              }}
            >
              We're sorry — the page didn't load correctly. Refreshing usually
              brings it back. If it keeps happening, please write to{' '}
              <a
                href="mailto:admin@myleaflegacy.info"
                style={{ color: '#1A3626', textDecoration: 'underline' }}
              >
                admin@myleaflegacy.info
              </a>{' '}
              and we'll have a look.
            </p>
            <div
              style={{
                marginTop: 24,
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={this.handleRetry}
                data-testid="error-retry-btn"
                style={{
                  background: '#1A3626',
                  color: '#FFFFFF',
                  fontSize: 11,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  padding: '12px 22px',
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Try again
              </button>
              <a
                href="/"
                data-testid="error-home-link"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: '#FFFFFF',
                  color: '#1A3626',
                  fontSize: 11,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  padding: '12px 22px',
                  borderRadius: 999,
                  border: '1px solid rgba(26,54,38,0.18)',
                  textDecoration: 'none',
                }}
              >
                Go home
              </a>
            </div>
            {process.env.NODE_ENV !== 'production' && (
              <details
                style={{
                  marginTop: 22,
                  textAlign: 'left',
                  fontSize: 11,
                  color: 'rgba(26,54,38,0.65)',
                }}
              >
                <summary style={{ cursor: 'pointer' }}>Error detail</summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    marginTop: 8,
                    background: '#F7F2E7',
                    padding: 10,
                    borderRadius: 6,
                    fontSize: 10,
                  }}
                >
                  {String(msg)}
                  {this.state.errorInfo && this.state.errorInfo.componentStack
                    ? `\n${this.state.errorInfo.componentStack}`
                    : ''}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return (
      <React.Fragment key={this.state.retryKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}
